import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { ACTIONS, EVENTS, Joyride, STATUS } from "react-joyride";
import type { EventData } from "react-joyride";
import { useMediaQuery } from "@mui/material";
import { useLocation } from "react-router-dom";
import { LiveRegion } from "components/live-region";
import { isOfferActive } from "helpers/methods";
import history from "utils/history";
import { TOURS, getTourById } from "../config";
import {
  currentPathname,
  isRouteActive,
  toursForRoute,
} from "../runtime/route-matcher";
import {
  isTourEligible,
  resolveTour,
  stepTargetProbe,
} from "../runtime/step-resolver";
import { waitForElement, watchForElement } from "../runtime/wait-for-element";
import { resolveTargetElement } from "../runtime/target-resolver";
import { closeReveal, openReveal } from "../runtime/step-reveal";
import {
  hasBlockingDialog,
  isPageSettled,
  withPresentTargets,
} from "../runtime/tour-gating";
import {
  clearTourCompletion,
  markTourCompleted,
  readTourState,
} from "../runtime/tour-storage";
import {
  AUTO_START_WATCH_INTERVAL_MS,
  AUTO_START_WATCH_TIMEOUT_MS,
  DEFAULT_TARGET_WAIT_MS,
  ROUTE_SETTLE_MS,
} from "../runtime/constants";
import {
  TOUR_LOCALE,
  buildFloatingOptions,
  buildJoyrideOptions,
  buildJoyrideStyles,
} from "../styles/tour.styles";
import useTourEvalContext from "../hooks/use-tour-context";
import TourContext from "./TourContext";
import TourTooltip from "./TourTooltip";
import TourModal from "./TourModal";
import type {
  ResolvedTourStep,
  TourContextValue,
  TourPhase,
  TourStepReveal,
} from "../types/tour.types";

interface PendingTransition {
  index: number;
  route: string;
}

interface TourRuntimeProps {
  children: ReactNode;
}

const TourRuntime = ({ children }: TourRuntimeProps) => {
  const location = useLocation();
  const evalContext = useTourEvalContext();
  const { featuresLoaded, isMobile, layout } = evalContext;

  const [activeTourId, setActiveTourId] = useState<string | null>(null);
  const [activeSteps, setActiveSteps] = useState<ResolvedTourStep[]>([]);
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [phase, setPhase] = useState<TourPhase>("idle");
  const [announcement, setAnnouncement] = useState<string>("");
  const [startIndex, setStartIndex] = useState<number>(0);

  const pendingRef = useRef<PendingTransition | null>(null);

  const endedRef = useRef<boolean>(true);
  const cancelWaitRef = useRef<(() => void) | null>(null);

  const autoStartAttempted = useRef<Set<string>>(new Set());
  const autoStartArmed = useRef<Map<string, () => void>>(new Map());

  const activeTourIdRef = useRef<string | null>(null);

  const lastRouteChangeAt = useRef<number>(Date.now());
  const pendingAutoStartRef = useRef<string | null>(null);

  const runStartedAutomatically = useRef<boolean>(false);
  const focusBeforeTour = useRef<HTMLElement | null>(null);
  const openRevealRef = useRef<TourStepReveal | null>(null);
  const revealTokenRef = useRef<number>(0);

  const activeStepsRef = useRef<ResolvedTourStep[]>([]);
  const displayedAnyStep = useRef<boolean>(false);
  const dropStepRef = useRef<((index: number) => void) | null>(null);
  const startTourRef = useRef<(tourId: string) => void>(() => {});

  const activeTour = activeTourId ? getTourById(activeTourId) : undefined;

  const eligibleTours = useMemo(
    () => TOURS.filter((tour) => isTourEligible(tour, evalContext)),
    [evalContext],
  );

  const availableTours = useMemo(
    () => toursForRoute(eligibleTours, location.pathname),
    [eligibleTours, location.pathname],
  );

  const joyrideSteps = useMemo(
    () => activeSteps.map((entry) => entry.step),
    [activeSteps],
  );

  const hasOfferBanner = isOfferActive();
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );
  const joyrideOptions = useMemo(
    () => buildJoyrideOptions({ hasOfferBanner, prefersReducedMotion }),
    [hasOfferBanner, prefersReducedMotion],
  );
  const joyrideStyles = useMemo(() => buildJoyrideStyles(), []);
  const joyrideFloating = useMemo(() => buildFloatingOptions(), []);
  const announce = useCallback((text: string) => {
    setAnnouncement((previous) => (previous === text ? `${text} ` : text));
  }, []);

  const abortPendingWait = useCallback(() => {
    cancelWaitRef.current?.();
    cancelWaitRef.current = null;
    pendingRef.current = null;
  }, []);

  const closeOpenReveal = useCallback(() => {
    const reveal = openRevealRef.current;
    openRevealRef.current = null;
    if (reveal) closeReveal(reveal);
  }, []);

  const showStep = useCallback(
    async (index: number, entry: ResolvedTourStep) => {
      const token = ++revealTokenRef.current;

      abortPendingWait();
      closeOpenReveal();

      if (entry.config.reveal) {
        setPhase("awaiting-target");
        openRevealRef.current = entry.config.reveal;
        await openReveal(entry.config.reveal);
        if (token !== revealTokenRef.current) return;
      }

      const probe = stepTargetProbe(entry.config);
      if (!probe()) {
        setPhase("awaiting-target");
        const waiter = waitForElement(
          probe,
          entry.step.targetWaitTimeout ?? DEFAULT_TARGET_WAIT_MS,
        );
        cancelWaitRef.current = waiter.cancel;
        const element = await waiter.promise;
        if (token !== revealTokenRef.current) return;
        cancelWaitRef.current = null;
        if (!element) {
          dropStepRef.current?.(index);
          return;
        }
      }

      displayedAnyStep.current = true;
      setStepIndex(index);
      setIsRunning(true);
      setPhase("running");
    },
    [abortPendingWait, closeOpenReveal],
  );

  const isTourCompleted = useCallback((tourId: string): boolean => {
    const tour = getTourById(tourId);
    if (!tour) return false;
    return readTourState().completed[tourId] === tour.version;
  }, []);

  const endTour = useCallback(
    (tourId: string | null, outcome: "finished" | "skipped" | "stopped") => {
      if (endedRef.current) return;
      endedRef.current = true;
      abortPendingWait();
      revealTokenRef.current += 1;
      closeOpenReveal();

      const endingTour = tourId ? getTourById(tourId) : undefined;

      if (tourId && endingTour && outcome !== "stopped") {
        markTourCompleted(tourId, endingTour.version);
      }

      setIsRunning(false);
      setPhase("idle");
      setActiveTourId(null);
      activeStepsRef.current = [];
      setActiveSteps([]);
      setStepIndex(0);
      const label = endingTour?.name ?? "Tour";
      announce(
        outcome === "finished" ? `${label} finished.` : `${label} ended.`,
      );

      const previous = focusBeforeTour.current;
      focusBeforeTour.current = null;
      if (previous && previous.isConnected) {
        window.setTimeout(() => previous.focus(), 0);
      }
    },
    [abortPendingWait, announce, closeOpenReveal],
  );

  const transitionTo = useCallback(
    (nextIndex: number, steps: ResolvedTourStep[]) => {
      const target = steps[nextIndex];
      if (!target) return;

      if (isRouteActive(target.route, currentPathname())) {
        pendingRef.current = null;
        void showStep(nextIndex, target);
        return;
      }

      abortPendingWait();
      revealTokenRef.current += 1;
      closeOpenReveal();
      pendingRef.current = { index: nextIndex, route: target.route };
      setIsRunning(false);
      setPhase("navigating");
      announce("Continuing the tour on the next page…");
      history.push(target.route);
    },
    [abortPendingWait, announce, closeOpenReveal, showStep],
  );

  const startTour = useCallback(
    async (tourId: string, options?: { fromStep?: number }) => {
      const tour = getTourById(tourId);
      if (!tour) return;
      if (!isTourEligible(tour, evalContext)) return;

      const all = await resolveTour(tour, evalContext);
      const resolved = isPageSettled(tour) ? withPresentTargets(all) : all;

      if (resolved.length === 0) {
        pendingAutoStartRef.current = null;
        return;
      }

      const pathname = currentPathname();
      const firstOnThisRoute = resolved.findIndex((entry) =>
        isRouteActive(entry.route, pathname),
      );
      const requested =
        options?.fromStep ?? (firstOnThisRoute >= 0 ? firstOnThisRoute : 0);
      const startAt = Math.min(Math.max(requested, 0), resolved.length - 1);

      focusBeforeTour.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;

      endedRef.current = false;
      displayedAnyStep.current = false;
      activeStepsRef.current = resolved;
      setActiveTourId(tourId);
      setActiveSteps(resolved);
      setStartIndex(startAt);

      if (pendingAutoStartRef.current !== tourId) {
        pendingAutoStartRef.current = null;
      }
      runStartedAutomatically.current = pendingAutoStartRef.current === tourId;

      if (tour.welcome && runStartedAutomatically.current) {
        setIsRunning(false);
        setPhase("welcome");
        return;
      }

      transitionTo(startAt, resolved);
    },
    [evalContext, transitionTo],
  );

  const restartTour = useCallback(
    async (tourId: string) => {
      clearTourCompletion(tourId);
      await startTour(tourId, { fromStep: 0 });
    },
    [startTour],
  );

  const beginSteps = useCallback(() => {
    if (!activeTourId) return;
    transitionTo(startIndex, activeSteps);
  }, [activeTourId, activeSteps, startIndex, transitionTo]);

  const finishTour = useCallback(() => {
    abortPendingWait();
    revealTokenRef.current += 1;
    closeOpenReveal();
    setIsRunning(false);

    const tour = activeTourId ? getTourById(activeTourId) : undefined;
    const shown = displayedAnyStep.current;

    if (activeTourId && tour && shown) {
      markTourCompleted(activeTourId, tour.version);
    }

    if (tour?.complete && runStartedAutomatically.current && shown) {
      setPhase("complete");
      return;
    }

    endTour(activeTourId, shown ? "finished" : "stopped");
  }, [activeTourId, abortPendingWait, closeOpenReveal, endTour]);

  const closeCompletion = useCallback(
    (route?: string) => {
      endTour(activeTourId, "stopped");
      if (route) history.push(route);
    },
    [activeTourId, endTour],
  );

  const handleEvent = useCallback(
    (data: EventData) => {
      const { type, action, index, status } = data;

      if (type === EVENTS.TOUR_END) {
        endTour(
          activeTourId,
          status === STATUS.SKIPPED ? "skipped" : "finished",
        );
        return;
      }

      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        if (action === ACTIONS.SKIP || action === ACTIONS.CLOSE) {
          endTour(activeTourId, "skipped");
          return;
        }
        if (!activeTourId) return;

        const next = index + (action === ACTIONS.PREV ? -1 : 1);
        if (next < 0) return;

        if (next >= activeSteps.length) {
          finishTour();
          return;
        }

        transitionTo(next, activeSteps);
      }
    },
    [activeTourId, activeSteps, endTour, finishTour, transitionTo],
  );

  useLayoutEffect(() => {
    dropStepRef.current = (index: number) => {
      const tourId = activeTourIdRef.current;
      if (!tourId) return;

      const remaining = activeStepsRef.current.filter((_, i) => i !== index);
      activeStepsRef.current = remaining;
      setIsRunning(false);
      setActiveSteps(remaining);

      if (index >= remaining.length) {
        finishTour();
        return;
      }
      transitionTo(index, remaining);
    };
  }, [finishTour, transitionTo]);

  useLayoutEffect(() => {
    startTourRef.current = (tourId: string) => {
      void startTour(tourId);
    };
  }, [startTour]);

  useEffect(() => {
    const pending = pendingRef.current;
    if (phase !== "navigating" || !pending) return;
    if (!isRouteActive(pending.route, location.pathname)) return;

    const target = activeSteps[pending.index];
    if (!target) {
      pendingRef.current = null;
      return;
    }

    pendingRef.current = null;
    setPhase("awaiting-target");
    void showStep(pending.index, target);
  }, [location.pathname, phase, activeSteps, showStep]);

  useEffect(() => {
    if (phase !== "running" || !activeTourId) return;
    const current = activeSteps[stepIndex];
    if (!current) return;
    if (isRouteActive(current.route, location.pathname)) return;
    endTour(activeTourId, "stopped");
  }, [location.pathname, phase, stepIndex, activeSteps, activeTourId, endTour]);

  useEffect(() => {
    activeTourIdRef.current = activeTourId;
  }, [activeTourId]);

  useEffect(() => {
    lastRouteChangeAt.current = Date.now();
  }, [location.pathname]);

  useEffect(() => {
    const pending = pendingAutoStartRef.current;
    if (!pending || pending !== activeTourId) return;
    if (phase !== "welcome" && phase !== "running") return;

    const tour = getTourById(pending);
    if (!tour) return;
    pendingAutoStartRef.current = null;
    markTourCompleted(pending, tour.version);
  }, [activeTourId, phase]);

  useEffect(() => {
    const disarm = (tourId: string) => {
      autoStartArmed.current.get(tourId)?.();
      autoStartArmed.current.delete(tourId);
    };

    autoStartArmed.current.forEach((_cancel, tourId) => {
      const armedTour = getTourById(tourId);
      if (!armedTour || !isRouteActive(armedTour.route, location.pathname)) {
        disarm(tourId);
      }
    });

    if (!featuresLoaded) return;
    if (isMobile) return;
    if (layout === "horizontal") return;

    if (readTourState().autoStartOptOut) return;

    availableTours.forEach((candidate) => {
      if (!candidate.autoStart) return;
      if (autoStartAttempted.current.has(candidate.id)) return;
      if (autoStartArmed.current.has(candidate.id)) return;
      if (!isRouteActive(candidate.route, location.pathname)) return;
      if (isTourCompleted(candidate.id)) return;

      const fire = () => {
        if (activeTourIdRef.current || hasBlockingDialog()) {
          const retry = window.setTimeout(fire, AUTO_START_WATCH_INTERVAL_MS);
          autoStartArmed.current.set(candidate.id, () =>
            window.clearTimeout(retry),
          );
          return;
        }
        autoStartArmed.current.delete(candidate.id);
        autoStartAttempted.current.add(candidate.id);
        pendingAutoStartRef.current = candidate.id;
        startTourRef.current(candidate.id);
      };

      if (candidate.autoStartWhen) {
        const key = candidate.autoStartWhen;
        const watcher = watchForElement(
          () => resolveTargetElement(key),
          AUTO_START_WATCH_INTERVAL_MS,
          AUTO_START_WATCH_TIMEOUT_MS,
        );
        autoStartArmed.current.set(candidate.id, watcher.cancel);
        watcher.promise.then((element) => {
          if (!element) {
            autoStartArmed.current.delete(candidate.id);
            return;
          }
          const sinceRoute = Date.now() - lastRouteChangeAt.current;
          const remaining = Math.max(0, ROUTE_SETTLE_MS - sinceRoute);
          const settle = window.setTimeout(fire, remaining);
          autoStartArmed.current.set(candidate.id, () =>
            window.clearTimeout(settle),
          );
        });
        return;
      }

      const timer = window.setTimeout(fire, ROUTE_SETTLE_MS);
      autoStartArmed.current.set(candidate.id, () =>
        window.clearTimeout(timer),
      );
    });
  }, [
    availableTours,
    location.pathname,
    featuresLoaded,
    isMobile,
    layout,
    isTourCompleted,
  ]);

  useEffect(
    () => () => {
      autoStartArmed.current.forEach((cancel) => cancel());
      autoStartArmed.current.clear();
      cancelWaitRef.current?.();
    },
    [],
  );

  const contextValue = useMemo<TourContextValue>(
    () => ({
      availableTours,
      startTour,
      restartTour,
      isTourCompleted,
    }),
    [availableTours, startTour, restartTour, isTourCompleted],
  );

  return (
    <TourContext.Provider value={contextValue}>
      {children}
      {isRunning && joyrideSteps.length > 0 && (
        <Joyride
          continuous
          run={isRunning}
          stepIndex={stepIndex}
          steps={joyrideSteps}
          options={joyrideOptions}
          floatingOptions={joyrideFloating}
          styles={joyrideStyles}
          locale={TOUR_LOCALE}
          tooltipComponent={TourTooltip}
          scrollToFirstStep
          onEvent={handleEvent}
        />
      )}
      {activeTour?.welcome && (
        <TourModal
          open={phase === "welcome"}
          variant="welcome"
          config={activeTour.welcome}
          stepCount={activeSteps.length}
          onPrimary={beginSteps}
          onSecondary={() => endTour(activeTourId, "skipped")}
          onClose={() => endTour(activeTourId, "skipped")}
        />
      )}
      {activeTour?.complete && (
        <TourModal
          open={phase === "complete"}
          variant="complete"
          config={activeTour.complete}
          stepCount={activeSteps.length}
          onPrimary={() => closeCompletion(activeTour.complete?.primaryRoute)}
          onSecondary={() =>
            closeCompletion(activeTour.complete?.secondaryRoute)
          }
          onClose={() => closeCompletion()}
        />
      )}
      <LiveRegion
        id="tour-live-region"
        message={announcement}
        priority="polite"
      />
    </TourContext.Provider>
  );
};

export default TourRuntime;
